import cronParser from 'cron-parser';
import { useEffect, useMemo, useRef } from 'react';
import { RiInformation2Line } from 'react-icons/ri';
import { Hint, HintIcon } from '@/components/primitives/hint';
import { DaysOfWeek } from '@/components/workflow-editor/steps/digest-delay-tabs/days-of-week';
import { NumbersPicker } from '@/components/workflow-editor/steps/digest-delay-tabs/numbers-picker';
import { Period } from '@/components/workflow-editor/steps/digest-delay-tabs/period';
import {
  getCronBasedOnPeriod,
  getPeriodFromCronParts,
  PeriodValues,
  parseCronString,
  toCronFields,
  toUiFields,
  UiCronFields,
} from '@/components/workflow-editor/steps/digest-delay-tabs/utils';

export const ScheduledType = ({
  value,
  isDisabled,
  isDigest,
  onValueChange,
  onError,
}: {
  value: string;
  isDisabled?: boolean;
  isDigest: boolean;
  onValueChange: (cron: string) => void;
  onError?: (error: unknown) => void;
}) => {
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  const { period, periodError } = useMemo(() => {
    try {
      const cronParts = parseCronString(value);

      return { period: getPeriodFromCronParts(cronParts), periodError: null };
    } catch (e) {
      return { period: PeriodValues.MINUTE, periodError: e };
    }
  }, [value]);

  const { second, month, dayOfMonth, dayOfWeek, hour, minute, fieldsError } = useMemo(() => {
    try {
      const expression = cronParser.parseExpression(value);

      return { ...toUiFields(expression.fields), fieldsError: null };
    } catch (e) {
      return {
        second: [],
        minute: [],
        hour: [],
        dayOfMonth: [],
        month: [],
        dayOfWeek: [],
        fieldsError: e,
      };
    }
  }, [value]);

  useEffect(() => {
    const error = periodError ?? fieldsError;
    if (error) {
      onErrorRef.current?.(error);
    }
  }, [periodError, fieldsError]);

  const handleValueChange = (fields: Partial<UiCronFields>) => {
    const cronFields = toCronFields({
      second,
      minute,
      hour,
      dayOfWeek,
      dayOfMonth,
      month,
      ...fields,
    });

    onValueChange(cronParser.fieldsToExpression(cronFields).stringify());
  };

  const handlePeriodChange = (period: string) => {
    onValueChange(getCronBasedOnPeriod(period as PeriodValues, { second, minute, hour, dayOfWeek, dayOfMonth, month }));
  };

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-x-1 gap-y-2">
        <div className="flex items-center gap-1">
          <span className="text-foreground-600 text-xs font-medium">{isDigest ? 'Every' : 'Until'}</span>
          <Period value={period} onPeriodChange={handlePeriodChange} isDisabled={isDisabled} />
        </div>
        {period !== PeriodValues.HOUR && period !== PeriodValues.MONTH && <span className="min-w-full" />}
        {period === PeriodValues.MONTH && (
          <div className="ml-auto flex items-center gap-1">
            <span className="text-foreground-600 text-xs font-medium">on</span>
            <NumbersPicker
              numbers={dayOfMonth}
              length={31}
              label="day(s)"
              onNumbersChange={(value) => {
                handleValueChange({ dayOfMonth: value });
              }}
              isDisabled={isDisabled}
            />
          </div>
        )}
        {(period === PeriodValues.MONTH || period === PeriodValues.WEEK) && (
          <div className="col-span-2 flex min-w-full items-center gap-1">
            <span className="text-foreground-600 text-xs font-medium">and</span>
            <DaysOfWeek
              daysOfWeek={dayOfWeek}
              onDaysChange={(value) => {
                handleValueChange({ dayOfWeek: value });
              }}
              isDisabled={isDisabled}
            />
          </div>
        )}
        {period !== PeriodValues.HOUR && period !== PeriodValues.MINUTE && (
          <div className="flex items-center gap-1">
            <span className="text-foreground-600 text-xs font-medium">at</span>
            <NumbersPicker
              numbers={hour}
              length={24}
              label="hour(s)"
              onNumbersChange={(value) => {
                handleValueChange({ hour: value });
              }}
              zeroBased
              isDisabled={isDisabled}
            />
          </div>
        )}
        {period !== PeriodValues.MINUTE && (
          <div className="flex items-center gap-1">
            <span className="text-foreground-600 text-xs font-medium">{period === PeriodValues.HOUR ? 'at' : ':'}</span>
            <NumbersPicker
              numbers={minute}
              length={60}
              label="minute(s)"
              onNumbersChange={(value) => {
                handleValueChange({ minute: value });
              }}
              zeroBased
              isDisabled={isDisabled}
            />
          </div>
        )}
      </div>
      <Hint className="text-text-soft text-2xs">
        <HintIcon as={RiInformation2Line} />
        Delivered in subscriber's timezone
      </Hint>
    </div>
  );
};
